import { Link } from "react-router-dom";
import { Menu, User } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useI18n } from "@/lib/i18n";
import { useNotificationsCount } from "@/hooks/useNotificationsCount";
import Logo from "@/components/Logo";
import { NotificationBell } from "@/components/NotificationBell";

interface TopBarProps {
  onMenuToggle: () => void;
  showAuth?: boolean;
  isLoggedIn?: boolean;
  sidebarOpen?: boolean;
}

export function TopBar({ onMenuToggle, showAuth = true, isLoggedIn = false, sidebarOpen = false }: TopBarProps) {
  const { t } = useI18n();
  const isMobile = useIsMobile();
  const unreadCount = useNotificationsCount();

  return (
    <header className="flex items-center justify-between px-4 h-12">
      <div className="flex items-center gap-0.5 sm:gap-2">
        <button
          onClick={onMenuToggle}
          className={`relative p-1.5 sm:p-2 rounded-lg hover:bg-accent transition-colors text-muted-foreground -ml-1 sm:ml-0 ${sidebarOpen ? "lg:hidden" : ""}`}
        >
          <Menu className="w-5 h-5" />
          {unreadCount > 0 && (
            <span className="absolute top-0.5 right-0.5 w-2 h-2 bg-destructive rounded-full" />
          )}
        </button>
        <Link
          to="/"
          className="flex items-center gap-1 text-foreground text-base hover:opacity-70 transition-opacity"
        >
          <Logo size={32} />
          <span className="font-semibold">cenik.ai</span>
        </Link>
      </div>

      <div className="flex items-center gap-1.5 sm:gap-2">
        {isLoggedIn ? (
          <Link
            to="/profile"
            className="relative p-2 rounded-full hover:bg-accent transition-colors text-muted-foreground"
            title={t("sidebar.settings")}
          >
            <User className="w-5 h-5" />
            <span className="absolute top-1 right-1 w-2.5 h-2.5 bg-primary rounded-full border-2 border-background" />
          </Link>
        ) : showAuth ? (
          <>
            {isMobile ? (
              <Link
                to="/login"
                className="p-2 rounded-full hover:bg-accent transition-colors text-muted-foreground"
              >
                <User className="w-5 h-5" />
              </Link>
            ) : (
              <Link
                to="/login"
                className="px-4 py-1.5 text-sm font-medium border border-border text-foreground rounded-full hover:bg-accent transition-colors"
              >
                {t("topbar.login")}
              </Link>
            )}
            <Link
              to="/login"
              className="hidden sm:inline-flex px-4 py-1.5 text-sm font-medium border border-border text-foreground rounded-full hover:bg-accent transition-colors"
            >
              {t("topbar.register")}
            </Link>
          </>
        ) : null}
        <NotificationBell />
      </div>
    </header>
  );
}
